import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { Box, Button, CardMedia, Stack, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useMemo, useRef } from 'react';
import { videoOverlaySX, videoPlayButtonSX, videoPlayerSX, ytContainerSX , pdpVideoContainerSX} from './styles/videoStyles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useProduct } from '@/data/Content/Product';
import CloseIcon from '@mui/icons-material/Close';

export const PdpExternalVideoRv: FC<{
	id: ID;
}> = ({ id }) => {

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const { product } = useProduct({ id: id.toString() });
	const PdpExternalVideoLabels = useLocalization('PdpExternalVideo').Labels;

	const attachments = product?.attachments ?? [];

	const theme = useTheme();
	const isLandscape = useMediaQuery("(orientation: landscape)");
	const pdpPlayerZindex = theme.zIndex.pdpVideoPlayer;

	const {
		videoSrc,
		isYoutubeVideo,
		youtubePlayerAttribute_src,
	} = useMemo(() => {
		const { youtubeMatch, icecatMatch, playMatch, iniMatch } = attachments.reduce(
			(acc, { attachmentAssetPath: url }) => {
				
				if (!url || (!url.includes("mp4") && !url.includes("www.youtube.com"))) {
					return acc;
				}

				acc.youtubeMatch ||= url.includes("www.youtube.com") ? url : acc.youtubeMatch;
				acc.icecatMatch ||= url.includes("objects.icecat.biz") ? url : acc.icecatMatch;
				acc.playMatch ||= url.includes("play.intertoys.nl") ? url : acc.playMatch;
				acc.iniMatch ||= url.includes("inishop.com") ? url : acc.iniMatch;

				return acc;
			},
			{
				youtubeMatch: undefined as string | undefined,
				icecatMatch: undefined as string | undefined,
				playMatch: undefined as string | undefined,
				iniMatch: undefined as string | undefined,
			}
		);

		const videoSrc = youtubeMatch ?? icecatMatch ?? playMatch ?? iniMatch; // assign based on priority

		let youtubePlayerAttribute_src = "";

		if (youtubeMatch) {
			const youtubeVideoId = youtubeMatch?.includes("/embed/")
				? youtubeMatch.substring(youtubeMatch.indexOf("/embed/") + 7)
				: "";
			youtubePlayerAttribute_src = `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&mute=1&controls=1`;
		}

		return {
			videoSrc,
			isYoutubeVideo: !!youtubeMatch,
			youtubePlayerAttribute_src,
		};
	}, [attachments]);

	const playPauseVideo = useCallback((play: boolean, isYoutubeVideo: boolean) => {

		// overlay toggle
		const overlayEle = document.getElementById("icecatVideoOverlay");
		if (overlayEle) {
			overlayEle.style.display = play ? "block" : "none";
		}

		const pdpVideoContainer = document.getElementById("pdp_video_container");
		if (pdpVideoContainer) {
			pdpVideoContainer.style.display = play ? "block" : "none";
		}

		// YouTube video
		if (isYoutubeVideo) {
			const youtubePlayer = document.getElementById("youtube_player_iframe") as HTMLIFrameElement | null;
			if (youtubePlayer) {
				youtubePlayer.src = play ? youtubePlayerAttribute_src : "about:blank";
			}
			return;
		}

		// HTML5 video
		const video = videoRef.current;
		if (!video) return;

		if (play) {
			video.currentTime = 0;
			video.play();
		} else {
			video.pause();
		}
	}, [videoRef, youtubePlayerAttribute_src]);

	return videoSrc ? (
		<Stack direction="row" spacing={1}>
			<Button 
				variant="semiConversion"
				data-testid="iceCatVideoPlayerbtn"
				id="iceCatVideoPlayerbtn"
				sx={videoPlayButtonSX}
				onClick={() => playPauseVideo(true, isYoutubeVideo)}
				aria-label={PdpExternalVideoLabels.PlayVideo.t()}
				startIcon={<PlayArrowIcon />}
			>
				{PdpExternalVideoLabels.PlayVideo.t()}
			</Button>
			<Box id="icecatVideoOverlay" onClick={() => playPauseVideo(false, isYoutubeVideo)} sx={videoOverlaySX}></Box>
			<Stack id="pdp_video_container"  sx={pdpVideoContainerSX({ isLandscape, pdpPlayerZindex })} display='none'>
				<Button	
					variant='textButton' 
					className='close-video-player-button' 
					startIcon={<CloseIcon />}
					onClick={()=>playPauseVideo(false, isYoutubeVideo)}
				></Button>
				{isYoutubeVideo ?
					<Stack id="container" sx={ytContainerSX}>
						<iframe 
							id="youtube_player_iframe" 
							width="100%" 
							height="100%" 
							frameBorder="0" 
							allowFullScreen 
							allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							referrerPolicy="strict-origin-when-cross-origin"
							loading="lazy"
						></iframe>
					</Stack>
					:
					<Stack>
						<CardMedia 
							id="iceCatVideoPlayer" 
							ref={videoRef} 
							src={videoSrc}
							sx={videoPlayerSX({isLandscape})}
							component="video" 
							loop
							controls
						/>
					</Stack>
				}
			</Stack>
		</Stack>
	) : null
};